import {
  Bell,
  Bot,
  Calculator,
  Flame,
  Home,
  Lock,
  Search,
  Sparkles,
  Store,
  TrendingUp,
  Users,
} from 'lucide-react';
import { NavLink } from 'react-router-dom';

interface NavItem {
  to: string;
  label: string;
  icon: typeof Home;
  badge?: { text: string; tone?: 'default' | 'alert' };
}

const OVERVIEW: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: Home },
  { to: '/settings', label: 'Stores', icon: Store },
  { to: '/trends', label: 'Trends', icon: Flame, badge: { text: 'new', tone: 'alert' } },
  { to: '/notifications', label: 'Notifications', icon: Bell },
  { to: '/automation', label: 'Automation', icon: Bot },
];

const TOOLS: NavItem[] = [
  { to: '/research', label: 'Research', icon: Search },
  { to: '/vendors', label: 'Vendors', icon: Users },
  { to: '/ai-tools', label: 'AI Tools', icon: Sparkles },
  { to: '/price-calc', label: 'Price Calc', icon: Calculator },
];

const ADMIN: NavItem[] = [{ to: '/admin/settings', label: 'Admin', icon: Lock }];

function NavLinkRow({ item }: { item: NavItem }): JSX.Element {
  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      style={({ isActive }) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '6px 8px',
        borderRadius: 6,
        fontSize: 13,
        color: isActive ? 'var(--text)' : 'var(--text-muted)',
        background: isActive ? 'var(--bg-card-hi)' : 'transparent',
        position: 'relative',
        marginBottom: 1,
        transition: 'background 0.12s, color 0.12s',
      })}
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span
              style={{
                position: 'absolute',
                left: -14,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 2,
                height: 16,
                background: 'var(--primary)',
                borderRadius: '0 2px 2px 0',
              }}
            />
          )}
          <item.icon size={14} strokeWidth={2} style={{ flexShrink: 0, opacity: 0.9 }} />
          <span>{item.label}</span>
          {item.badge && (
            <span
              style={{
                marginLeft: 'auto',
                fontSize: 10,
                background:
                  item.badge.tone === 'alert' ? 'var(--danger-bg)' : 'var(--bg-card-hi)',
                color: item.badge.tone === 'alert' ? 'var(--danger)' : 'var(--text-muted)',
                padding: '1px 6px',
                borderRadius: 10,
                fontWeight: 500,
              }}
            >
              {item.badge.text}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

function GroupLabel({ children }: { children: string }): JSX.Element {
  return (
    <div
      style={{
        fontSize: 10,
        color: 'var(--text-dim)',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        padding: '0 8px 8px',
        fontWeight: 500,
      }}
    >
      {children}
    </div>
  );
}

export function Sidebar(): JSX.Element {
  return (
    <aside
      style={{
        borderRight: '1px solid var(--border)',
        background: 'var(--bg)',
        padding: '18px 14px',
        position: 'sticky',
        top: 0,
        height: '100vh',
        overflowY: 'auto',
        width: 'var(--sidebar-w)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Brand */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 8px 22px' }}>
        <div
          style={{
            width: 28,
            height: 28,
            background: 'var(--primary)',
            borderRadius: 7,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--primary-on)',
            fontWeight: 700,
            fontSize: 14,
            letterSpacing: '-0.02em',
            position: 'relative',
          }}
        >
          e
          <span
            style={{
              content: '""',
              position: 'absolute',
              inset: -3,
              borderRadius: 9,
              background: 'var(--primary)',
              opacity: 0.2,
              animation: 'brand-pulse 2.4s ease-in-out infinite',
              pointerEvents: 'none',
            }}
          />
        </div>
        <div>
          <div style={{ fontWeight: 600, fontSize: 14, letterSpacing: '-0.01em' }}>ebayds</div>
          <div
            style={{
              fontSize: 10,
              color: 'var(--text-dim)',
              fontWeight: 400,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
            }}
          >
            v1 · ops
          </div>
        </div>
      </div>

      <div style={{ marginBottom: 18 }}>
        <GroupLabel>Overview</GroupLabel>
        {OVERVIEW.map((item) => (
          <NavLinkRow key={item.to} item={item} />
        ))}
      </div>

      <div style={{ marginBottom: 18 }}>
        <GroupLabel>Tools</GroupLabel>
        {TOOLS.map((item) => (
          <NavLinkRow key={item.to} item={item} />
        ))}
      </div>

      <div style={{ marginTop: 'auto', paddingTop: 14, borderTop: '1px solid var(--border)' }}>
        <GroupLabel>Admin</GroupLabel>
        {ADMIN.map((item) => (
          <NavLinkRow key={item.to} item={item} />
        ))}
      </div>

      {/* Foot brand line */}
      <div
        style={{
          marginTop: 14,
          paddingTop: 14,
          borderTop: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 10,
          color: 'var(--text-dim)',
        }}
      >
        <TrendingUp size={12} strokeWidth={2} />
        <span>dropship intelligence</span>
      </div>
    </aside>
  );
}
